import { is } from "@callcenter/helpers"
import { combine, createEvent, createStore, sample } from "effector"
import { createPersistStore } from "@callcenter/effector-helpers"
import { $callcenterService } from "@callcenter/effector-services"

import { selfServiceApi } from "../api"
import type { KycState, KycStatus } from "../shared/types"
import { isPendingStatus, isTerminalStatus } from "./kyc.helpers"
import {
  $isTrialExpired,
  $isVerificationModalShown,
  $mobileVerificationUrl,
  closeVerificationModal,
  emailModalClosed,
  qrModalClosed,
  qrModalOpened,
  tryAgainClicked,
  verificationModalOpened
} from "./self-service.model"

export const $kycState = $callcenterService.$settings.map<KycState>(
  (state) => state.kyc ?? { status: null, verification_url: null }
)

export const kycStatusReceived = createEvent<KycStatus>()
const $liveKycStatus = createStore<KycStatus | null>(null)

sample({ clock: kycStatusReceived, target: $liveKycStatus })

export const $effectiveKycStatus = combine($liveKycStatus, $kycState, (live, kyc) => live ?? kyc.status)

export const $kycVerificationUrl = $kycState.map((kyc) => kyc.verification_url)

export const $isKycReentry = combine(
  $effectiveKycStatus,
  $kycVerificationUrl,
  (status, url) => isPendingStatus(status) && Boolean(url)
)

export const $verifyModalDismissed = createStore(false)

export const $isVerifyModalVisible = combine(
  {
    isShown: $isVerificationModalShown,
    isReentry: $isKycReentry,
    dismissed: $verifyModalDismissed,
    isTrialExpired: $isTrialExpired
  },
  ({ isShown, isReentry, dismissed, isTrialExpired }) => isShown || (isReentry && isTrialExpired && !dismissed)
)

// Acknowledgement is persisted so the result overlay is shown once per attempt and does not
// reappear on refresh or navigation (the desktop flow returns from Shufti via a full reload).
// It is cleared whenever a new verification attempt starts so a later result shows again.
export const [$kycResultAcknowledged, kycResultAcknowledgedChanged] = createPersistStore<boolean>("ss_kyc_result_ack", {
  initialValue: false,
  contract: (raw): raw is boolean => is.boolean(raw)
})

export const $shouldShowKycResult = combine(
  $effectiveKycStatus,
  $kycResultAcknowledged,
  (status, acknowledged) => !acknowledged && isTerminalStatus(status)
)
export const $hasPendingKyc = $effectiveKycStatus.map(isPendingStatus)

export const dismissKycResult = createEvent()
export const continueKyc = createEvent()
export const retryKyc = createEvent()
export const showExistingKycQr = createEvent()

sample({
  clock: dismissKycResult,
  fn: () => true,
  target: kycResultAcknowledgedChanged
})

sample({
  clock: [selfServiceApi.requestUpgradeFx, selfServiceApi.startKycFx],
  fn: () => false,
  target: kycResultAcknowledgedChanged
})

sample({
  clock: retryKyc,
  target: tryAgainClicked
})

sample({
  clock: continueKyc,
  source: $kycVerificationUrl,
  filter: Boolean,
  target: selfServiceApi.redirectToKycFx
})

sample({
  clock: showExistingKycQr,
  source: $kycVerificationUrl,
  filter: Boolean,
  target: [$mobileVerificationUrl, qrModalOpened]
})

sample({
  clock: closeVerificationModal,
  fn: () => true,
  target: $verifyModalDismissed
})

sample({
  clock: kycStatusReceived,
  filter: (status) => isTerminalStatus(status),
  target: [closeVerificationModal, qrModalClosed, emailModalClosed]
})

sample({
  clock: kycStatusReceived,
  source: $kycVerificationUrl,
  filter: (url, status) => isPendingStatus(status) && Boolean(url),
  target: [verificationModalOpened, qrModalClosed]
})
